import {
    useState,
    MouseEvent,
    KeyboardEvent,
    useEffect,
    useCallback,
} from 'react'
import { Anchor } from '../components/DrawerStm'
import { useFSM } from './useFSM'
import { CommonValueType, GLOBAL_STATE_TYPE, ResetFN } from './types'
import { getTranslator } from '../i18n/useTranslation'
import { useProgress } from '../components/progress/useProgress'
import { DEFAULT_GLOBAL_STATE } from './defaultState'
import { info } from '../services/sentryLogger'
import { useAsgnState } from './useAsgnState'
import { getStatements } from '../i18n/Statements'

export const useApp = () => {
    const [globalState, setGlobalState] = useState<GLOBAL_STATE_TYPE>({
        ...DEFAULT_GLOBAL_STATE,
    })

    const { resetAsgnState, state, allStms, ...rest } = useAsgnState()
    const { params } = state
    const { asgnsForRound, setAsgnsForRound } = rest //FIXME

    const resetFn: ResetFN = useCallback(
        (options) => {
            const { fsmState, newParams, loginState } = options || {}
            info('resetFn() - Init deck and misc', {
                fsmState,
                newParams,
                loginState,
            })
            resetAsgnState(fsmState, newParams, loginState)
            setGlobalState({
                ...DEFAULT_GLOBAL_STATE,
                // Progress state
                progresses: [0, 0, 0, 0],
                progress: 0,
                steps: 0,
                remainingSteps: 0,
                remainingTime: 20 * 60,
                // login
                ...options?.loginState,
            })
        },
        [resetAsgnState]
    )

    const t = getTranslator(params.assessmentParams.langIndex)

    // RELOAD old save
    useEffect(() => {
        resetFn()
    }, [resetFn])

    const currentStateFSM = useFSM({
        ...rest,
        state,
        resetAsgnState,
        params,
        resetFn,
        allStms,
        // FIXME
        loginData: {
            token: globalState.token,
            slug: globalState.slug,
            source: globalState.source,
            from: globalState.from,
            userId: globalState.userId,
            userEmail: globalState.userEmail,
        },
    })

    console.debug('useApp.currentStateFSM=', currentStateFSM)

    const incrWeight = (id: string) => {
        info('incrWeight', id)
        // FIXME will re-render everything based on asgnsForRound ...
        setAsgnsForRound((prevAssignations) => {
            const stmIndex = prevAssignations.findIndex((a) => a.id === id)
            const newAssignations = [...prevAssignations]
            const prevAssignation = prevAssignations[stmIndex]
            newAssignations[stmIndex] = {
                ...newAssignations[stmIndex],
                weight: (prevAssignation?.weight ?? 0) + 1,
            }
            return newAssignations
        })
    }

    const decrWeight = (id: string) => {
        info('decrWeight', id)
        setAsgnsForRound((prevAssignations) => {
            const stmIndex = prevAssignations.findIndex((a) => a.id === id)
            const newAssignations = [...prevAssignations]
            const prevAssignation = prevAssignations[stmIndex]
            newAssignations[stmIndex] = {
                ...newAssignations[stmIndex],
                weight: (prevAssignation?.weight ?? 0) - 1,
            }
            return newAssignations
        })
    }

    const toggleDrawer =
        (anchor: Anchor) => (event: KeyboardEvent | MouseEvent) => {
            if (
                event.type === 'keydown' &&
                ((event as KeyboardEvent).key === 'Tab' ||
                    (event as KeyboardEvent).key === 'Shift')
            ) {
                return
            }
            // FIXME UIState
            setGlobalState((s) => ({ ...s, [anchor]: !s[anchor] }))
        }

    const handleDrag = (e: any, ui: any) => {
        info('handleDrag', e, ui)
        // FIXME UIState
        setGlobalState((prevState) => ({
            ...prevState,
            deltaPosition: {
                x: prevState.deltaPosition.x + ui.deltaX,
                y: prevState.deltaPosition.y + ui.deltaY,
            },
        }))
    }

    useProgress(
        state,
        rest.scannedItems,
        setGlobalState,
        // @ts-ignore
        (allStms?.['leadership']?.length ?? 0) +
            (allStms?.['workenv']?.length ?? 0) ??
            getStatements(params.assessmentParams['langIndex']).length
    )

    const toggled = (anchor: Anchor) => globalState[anchor]
    const isInProgress = globalState.remainingSteps > 0
    const currentLabel = `${state.currentStmIndex + 1}/${asgnsForRound.length}`
    const currentTitle = asgnsForRound[state.currentStmIndex]?.title
    const output = {
        ...globalState,
        ...rest,
        ...currentStateFSM,
        allStms,
        state,
        resetAsgnState,
        resetFn,
        isInProgress,
        currentTitle,
        // FIXME
        setGlobalState,
        toggleDrawer,
        toggled,
        handleDrag,
        currentLabel,
        decrWeight,
        incrWeight,
        // FIXME Unused
        // setProgress: (p: number) =>
        //     setGlobalState((s) => ({ ...s, progress: p })),
        // setProgresses: (ps: [r1: number, r2: number, r3: number, fr: number]) =>
        //     setGlobalState((s) => ({ ...s, progresses: ps })),
        t,
    } as CommonValueType
    return output
}
